import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase, signOutApp } from '../../lib/supabase.js';
import { promiseWithTimeout } from '../../lib/promiseWithTimeout.js';
import WebTopBar from '../components/WebTopBar.js';
import SurcosRating from '../components/SurcosRating.js';
import Icon from '../components/Icon.js';
import { DCard, SettingsRow, Badge, BtnGold, Toggle } from '../components/MarketplaceAtoms.js';
import PartnerVerifiedBadge from '../components/PartnerVerifiedBadge.js';
import { isDreamvinylVerifiedPartner } from '../utils/partnerVerified.js';
import ColeccionItemFormModal from '../components/ColeccionItemFormModal.js';
import { useProfile } from '../hooks/useProfile.js';
import { resolveProfileAvatarUrl } from '../../lib/profileAvatar.js';
import { groovesProgress, profileLevelLabel } from '../profileConstants.js';
import {
  levelFromGroovesTotal,
  groovesSurcoVisualCount,
  GROOVES_PHONE_GATE_USER_MESSAGE,
  GROOVES_LEDGER_REASON_LABELS,
  profileGroovesBalance,
  profileGroovesEarnedTotal,
} from '../constants/groovesEconomy.js';
import { listProfileColeccion, deleteProfileColeccionItem, updateProfileColeccionFlags } from '../../lib/profileColeccion.js';
import { buildProfilePublicLocationLine } from '../../lib/profileLocationPublic.js';
import { collectProfileSocialEntries } from '../../lib/profileSocialLinks.js';
import { mapListing } from '../../lib/listingsService.js';
import { canSellerEditListing } from '../listingPublicarHydrate.js';
import {
  fetchGroovesStripePacks,
  fulfillGroovesCheckoutSession,
  groovesPackDisplayLabel,
  groovesPackDisplayPrice,
  mapGroovesCheckoutError,
  startGroovesPackCheckout,
} from '../../lib/groovesStripeService.js';
import GroovesCanjeShop from '../components/GroovesCanjeShop.js';
import CosmeticBadgesRow from '../components/CosmeticBadgesRow.js';
import { fetchProfileCosmeticBadges } from '../../lib/groovesSpendService.js';

function formatEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function coleccionMetaLine(row) {
  const parts = [
    row.genre,
    row.format,
    row.condition_code,
    row.release_year ? `Año ${row.release_year}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** Misma miniatura que en la pestaña Colección: `cover_url` o icono de disco. */
function ColeccionItemThumb({ row }) {
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 8,
        background: 'linear-gradient(145deg,#1a2e1a,#0d1f2e)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
      {row.cover_url ? (
        <img src={row.cover_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Icon name="disc" size={18} color="rgba(200,168,75,0.5)" />
      )}
    </div>
  );
}

/** Perfil: «En venta» + colección con joyas/ofertas derivadas de flags en `profile_coleccion_items`. */
const PROFILE_TAB_EMPTY_BUCKETS = [[], [], [], []];

const PERFIL_DATA_TIMEOUT_MS = 22000;
/** Cabecera «Mi perfil»: avatar y vinilo de surcos Grooves comparten tamaño. */
const PERFIL_HEADER_AVATAR_PX = 64;

export default function PagePerfil({ onBack, onNavigate, user, paymentsEnabled = true }) {
  const { profile, loading: profileLoading, error: profileFetchError, refetch: refetchProfile } = useProfile(user?.id);
  const [tab, setTab] = useState(0);
  const tabs = ['En venta', 'Colección', 'Joyas', 'Ofertas'];
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [coleccion, setColeccion] = useState([]);
  const [coleccionLoading, setColeccionLoading] = useState(false);
  const [coleccionErr, setColeccionErr] = useState('');
  const [addColeccionOpen, setAddColeccionOpen] = useState(false);
  const [stats, setStats] = useState({ ventas: 0, compras: 0, seguidores: 0 });
  /** Hasta que terminen las consultas a Supabase, no mostramos números como definitivos. */
  const [statsLoading, setStatsLoading] = useState(!!user?.id);
  const [rating, setRating] = useState({ avg: null, count: 0 });
  const [groovesLedger, setGroovesLedger] = useState([]);
  const [groovesLedgerOffset, setGroovesLedgerOffset] = useState(0);
  const [groovesLedgerHasMore, setGroovesLedgerHasMore] = useState(false);
  const [groovesLedgerLoading, setGroovesLedgerLoading] = useState(false);
  const [groovesConfigDesc, setGroovesConfigDesc] = useState({});
  const [groovesPremiados, setGroovesPremiados] = useState([]);
  const [cosmeticBadgesEquipped, setCosmeticBadgesEquipped] = useState([]);
  const [groovePacks, setGroovePacks] = useState([]);
  const [groovePackBusy, setGroovePackBusy] = useState(null);
  const [grooveShopMsg, setGrooveShopMsg] = useState('');
  const [grooveShopErr, setGrooveShopErr] = useState('');
  const [groovePacksLoading, setGroovePacksLoading] = useState(false);
  const [levelChallenges, setLevelChallenges] = useState([]);
  const [challengeUnlockIds, setChallengeUnlockIds] = useState(() => new Set());
  const [challengeCompleteIds, setChallengeCompleteIds] = useState(() => new Set());
  const [challengesLoading, setChallengesLoading] = useState(false);
  /** Niveles de retos desplegados (clic en cabecera de nivel). */
  const [expandedChallengeLevels, setExpandedChallengeLevels] = useState(() => new Set());

  const goNav = item => {
    if (item.openFavorites) onNavigate('marketplace', { favoritesOnly: true });
    else onNavigate(item.nav);
  };

  useEffect(() => {
    if (!user?.id) {
      setListings([]);
      setStats({ ventas: 0, compras: 0, seguidores: 0 });
      setStatsLoading(false);
      setRating({ avg: null, count: 0 });
      return;
    }
    let cancelled = false;
    (async () => {
      setListingsLoading(true);
      setStatsLoading(true);
      try {
        const [listRes, salesRes, buysRes, folRes, revRes] = await promiseWithTimeout(
          Promise.all([
            supabase
              .from('listings')
              .select('id,title,artist,condition,price,status,cover_url,photos')
              .eq('seller_id', user.id)
              .eq('status', 'active')
              .order('created_at', { ascending: false })
              .limit(40),
            supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('seller_id', user.id),
            supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('buyer_id', user.id),
            supabase.from('follows').select('follower_id', { count: 'exact', head: true }).eq('following_id', user.id),
            supabase.from('reviews').select('rating').eq('reviewed_id', user.id).eq('is_visible', true),
          ]),
          PERFIL_DATA_TIMEOUT_MS,
          'Tiempo de espera al cargar datos del perfil.'
        );
        if (cancelled) return;
        setListings(listRes.data ?? []);
        if (listRes.error) console.warn('[PagePerfil] listings:', listRes.error.message);
        setStats({
          ventas: salesRes.error ? 0 : Number(salesRes.count ?? 0),
          compras: buysRes.error ? 0 : Number(buysRes.count ?? 0),
          seguidores: folRes.error ? 0 : Number(folRes.count ?? 0),
        });
        if (salesRes.error) console.warn('[PagePerfil] ventas (transactions):', salesRes.error.message);
        if (buysRes.error) console.warn('[PagePerfil] compras:', buysRes.error.message);
        if (folRes.error) console.warn('[PagePerfil] seguidores:', folRes.error.message);
        const rows = revRes.error ? [] : revRes.data ?? [];
        const count = rows.length;
        const avg = count ? rows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / count : null;
        setRating({ avg, count });
      } catch (e) {
        console.error('[PagePerfil] carga perfil:', e);
        if (!cancelled) {
          setListings([]);
          setStats({ ventas: 0, compras: 0, seguidores: 0 });
          setRating({ avg: null, count: 0 });
          if (e?.message) console.warn('[PagePerfil]', e.message);
        }
      } finally {
        if (!cancelled) {
          setListingsLoading(false);
          setStatsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      setListingsLoading(false);
      setStatsLoading(false);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || tab < 1 || tab > 3) return;
    let cancelled = false;
    setColeccionErr('');
    (async () => {
      setColeccionLoading(true);
      try {
        const rows = await listProfileColeccion(user.id);
        if (!cancelled) setColeccion(rows);
      } catch (e) {
        const msg = e?.message || '';
        if (!cancelled) {
          setColeccion([]);
          setColeccionErr(
            msg.includes('does not exist') || msg.includes('relation') || e?.code === '42P01'
              ? 'Falta la tabla en Supabase. Ejecuta la migración 20260423130000_profile_coleccion_items.sql en el SQL Editor.'
              : msg || 'No se pudo cargar tu colección.'
          );
        }
      } finally {
        if (!cancelled) setColeccionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, tab]);

  useEffect(() => {
    if (!user?.id) {
      setGroovesLedger([]);
      setGroovesLedgerOffset(0);
      setGroovesLedgerHasMore(false);
      setGroovesConfigDesc({});
      return;
    }
    let cancelled = false;
    (async () => {
      setGroovesLedgerLoading(true);
      try {
        const [{ data: cfgRows }, { data: ledRows }] = await Promise.all([
          supabase.from('grooves_config').select('reason, descripcion'),
          supabase
            .from('grooves_ledger')
            .select('id, delta, reason, created_at')
            .eq('profile_id', user.id)
            .order('created_at', { ascending: false })
            .range(0, 19),
        ]);
        if (cancelled) return;
        const dm = Object.fromEntries((cfgRows || []).map(r => [r.reason, r.descripcion || r.reason]));
        setGroovesConfigDesc(dm);
        const batch = Array.isArray(ledRows) ? ledRows : [];
        setGroovesLedger(batch);
        setGroovesLedgerOffset(batch.length);
        setGroovesLedgerHasMore(batch.length === 20);
      } catch (e) {
        console.warn('[PagePerfil] grooves ledger:', e);
        if (!cancelled) {
          setGroovesLedger([]);
          setGroovesLedgerOffset(0);
          setGroovesLedgerHasMore(false);
        }
      } finally {
        if (!cancelled) setGroovesLedgerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  /** Si Stripe cobró pero el webhook no abonó, recuperar al abrir el perfil. */
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data: pending } = await supabase
        .from('grooves_stripe_checkout_sessions')
        .select('stripe_session_id')
        .eq('profile_id', user.id)
        .is('fulfilled_at', null)
        .order('created_at', { ascending: false })
        .limit(3);
      if (cancelled || !pending?.length) return;
      for (const row of pending) {
        if (cancelled) break;
        const sid = String(row.stripe_session_id ?? '').trim();
        if (!sid.startsWith('cs_')) continue;
        const { ok, fulfilled } = await fulfillGroovesCheckoutSession(sid);
        if (ok && fulfilled) {
          await refetchProfile();
          const { data: ledRows } = await supabase
            .from('grooves_ledger')
            .select('id, delta, reason, created_at')
            .eq('profile_id', user.id)
            .order('created_at', { ascending: false })
            .range(0, 19);
          if (!cancelled && Array.isArray(ledRows)) {
            setGroovesLedger(ledRows);
            setGroovesLedgerOffset(ledRows.length);
            setGroovesLedgerHasMore(ledRows.length === 20);
          }
          setGrooveShopMsg('Compra confirmada: tus Grooves ya están en tu cuenta.');
          setGrooveShopErr('');
          break;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, refetchProfile]);

  useEffect(() => {
    if (!user?.id) {
      setGroovesPremiados([]);
      setCosmeticBadgesEquipped([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const [premRes, cosRes] = await Promise.all([
        supabase.rpc('list_grooves_premiados_public', { p_profile_id: user.id }),
        fetchProfileCosmeticBadges(user.id),
      ]);
      if (cancelled) return;
      if (premRes.error) {
        console.warn('[PagePerfil] grooves premiados:', premRes.error.message);
        setGroovesPremiados([]);
      } else {
        setGroovesPremiados(Array.isArray(premRes.data) ? premRes.data : []);
      }
      setCosmeticBadgesEquipped(cosRes.equipped ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setLevelChallenges([]);
      setChallengeUnlockIds(new Set());
      setChallengeCompleteIds(new Set());
      setChallengesLoading(false);
      return;
    }
    let cancelled = false;
    setChallengesLoading(true);
    (async () => {
      await supabase.rpc('grooves_evaluate_challenges', { p_profile_id: user.id });
      const [catRes, unlockRes, completeRes] = await Promise.all([
        supabase
          .from('grooves_level_challenges')
          .select('id, min_level, slug, title, description, grooves_reward, sort_order')
          .eq('activo', true)
          .order('min_level', { ascending: true })
          .order('sort_order', { ascending: true }),
        supabase.from('grooves_level_challenge_unlocks').select('challenge_id').eq('profile_id', user.id),
        supabase.from('grooves_level_challenge_completions').select('challenge_id').eq('profile_id', user.id),
      ]);
      if (cancelled) return;
      setLevelChallenges(catRes.data ?? []);
      setChallengeUnlockIds(new Set((unlockRes.data ?? []).map(r => r.challenge_id).filter(Boolean)));
      setChallengeCompleteIds(new Set((completeRes.data ?? []).map(r => r.challenge_id).filter(Boolean)));
      setChallengesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setGroovePacks([]);
      setGroovePacksLoading(false);
      return;
    }
    let cancelled = false;
    setGroovePacksLoading(true);
    (async () => {
      const { data, error } = await fetchGroovesStripePacks();
      if (cancelled) return;
      if (error) {
        console.warn('[PagePerfil] grooves_stripe_packs:', error);
        setGroovePacks([]);
      } else {
        setGroovePacks(Array.isArray(data) ? data : []);
      }
      setGroovePacksLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleBuyGroovesPack = useCallback(async code => {
    setGrooveShopMsg('');
    setGrooveShopErr('');
    setGroovePackBusy(code);
    try {
      const result = await startGroovesPackCheckout(code);
      if (!result.ok) {
        setGrooveShopErr(result.error || mapGroovesCheckoutError('invoke_failed'));
        setGroovePackBusy(null);
      }
      /* Si ok: redirección a Stripe; no quitar busy para evitar doble clic */
    } catch (e) {
      setGrooveShopErr(mapGroovesCheckoutError(e?.message || 'Error al iniciar el pago.'));
      setGroovePackBusy(null);
    }
  }, []);

  const loadMoreGroovesLedger = useCallback(async () => {
    if (!user?.id || groovesLedgerLoading || !groovesLedgerHasMore) return;
    setGroovesLedgerLoading(true);
    try {
      const from = groovesLedgerOffset;
      const { data } = await supabase
        .from('grooves_ledger')
        .select('id, delta, reason, created_at')
        .eq('profile_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, from + 19);
      const batch = Array.isArray(data) ? data : [];
      setGroovesLedger(prev => [...prev, ...batch]);
      setGroovesLedgerOffset(from + batch.length);
      setGroovesLedgerHasMore(batch.length === 20);
    } catch (e) {
      console.warn('[PagePerfil] grooves ledger más:', e);
    } finally {
      setGroovesLedgerLoading(false);
    }
  }, [user?.id, groovesLedgerLoading, groovesLedgerHasMore, groovesLedgerOffset]);

  const groovesTotal = profile ? profileGroovesBalance(profile) : 0;
  const groovesEarned = profile ? profileGroovesEarnedTotal(profile) : 0;
  const level = profile ? Math.min(5, Math.max(1, Number(profile.level) || 1)) : 1;
  const gProg = useMemo(() => groovesProgress(groovesEarned, level), [groovesEarned, level]);
  const groovesLevelDream = useMemo(
    () => ({ ...levelFromGroovesTotal(groovesEarned), level, label: profileLevelLabel(level) }),
    [groovesEarned, level]
  );
  const groovesSurcosVisuales = useMemo(() => groovesSurcoVisualCount(groovesEarned), [groovesEarned]);
  const groovesLedgerWithBalance = useMemo(() => {
    const B = Number(groovesTotal) || 0;
    let acc = 0;
    return groovesLedger.map(row => {
      const balanceAfter = B - acc;
      acc += Number(row.delta) || 0;
      return { ...row, balanceAfter };
    });
  }, [groovesLedger, groovesTotal]);

  const challengesByLevel = useMemo(() => {
    const m = new Map();
    for (const c of levelChallenges) {
      const lv = Number(c.min_level) || 1;
      if (!m.has(lv)) m.set(lv, []);
      m.get(lv).push(c);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [levelChallenges]);

  const toggleChallengeLevel = useCallback(lv => {
    setExpandedChallengeLevels(prev => {
      const next = new Set(prev);
      if (next.has(lv)) next.delete(lv);
      else next.add(lv);
      return next;
    });
  }, []);

  const displayName = profile?.display_name?.trim() || profile?.username || user?.user_metadata?.username || user?.email?.split('@')[0] || 'Usuario';
  const handle = profile?.username ? `@${profile.username}` : user?.email ? `@${user.email.split('@')[0]}` : '@usuario';
  const locationLine = buildProfilePublicLocationLine(profile);

  const valoracionMedia = rating.avg != null ? Math.round(rating.avg * 10) / 10 : null;
  /** Sin valoraciones visibles: 0 surcos (no inventar nota). */
  const valoracionSurcos =
    rating.count > 0 && valoracionMedia != null
      ? Math.min(5, Math.max(1, Math.round(valoracionMedia)))
      : 0;

  const statLabel = n => {
    if (!user?.id) return '—';
    if (statsLoading) return '…';
    return String(Number(n) || 0);
  };

  const itemsByTab = useMemo(() => {
    const base = PROFILE_TAB_EMPTY_BUCKETS.map(() => []);
    if (listings.length) {
      base[0] = listings.map(row => ({
        id: row.id,
        listing: mapListing({ ...row, profiles: null }),
        coverUrl:
          row.cover_url?.trim() ||
          (Array.isArray(row.photos) && row.photos.length ? String(row.photos[0]).trim() : null) ||
          null,
        title: row.title,
        artist: row.artist,
        cond: row.condition || '—',
        price: formatEuro(row.price),
      }));
    }
    return base;
  }, [listings]);

  const joyasItems = useMemo(() => coleccion.filter(c => c.is_joya), [coleccion]);
  const ofertasItems = useMemo(() => coleccion.filter(c => c.open_to_offers), [coleccion]);

  const handleColeccionFlag = useCallback(
    async (rowId, patch) => {
      if (!user?.id) return;
      setColeccionErr('');
      try {
        const updated = await updateProfileColeccionFlags(user.id, rowId, patch);
        if (updated) setColeccion(prev => prev.map(x => (x.id === rowId ? { ...x, ...updated } : x)));
      } catch (e) {
        const msg = e?.message || '';
        setColeccionErr(
          msg.includes('is_joya') || msg.includes('open_to_offers') || e?.code === '42703'
            ? 'Falta la migración de joyas/ofertas. Ejecuta 20260429120000_profile_coleccion_joya_oferta.sql en Supabase.'
            : msg || 'No se pudo actualizar.'
        );
      }
    },
    [user?.id]
  );

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar
        onBack={onBack}
        title="Mi perfil"
        mini
        dark
        user={user}
        onNavigate={onNavigate}
        onSignOut={onBack}
      />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 20px 60px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14, rowGap: 16, marginBottom: 20 }}>
          <div
            style={{
              width: PERFIL_HEADER_AVATAR_PX,
              height: PERFIL_HEADER_AVATAR_PX,
              borderRadius: '50%',
              background: '#1a1a2e',
              border: `2px solid ${C.gold}`,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 500,
              color: C.gold,
              flexShrink: 0,
            }}>
            {resolveProfileAvatarUrl(profile?.avatar_url) ? (
              <img src={resolveProfileAvatarUrl(profile.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : user ? (
              (displayName[0] || user.email?.[0] || '?').toUpperCase()
            ) : (
              'TU'
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 500, color: '#fff' }}>
              {profileLoading ? 'Cargando…' : profileFetchError ? 'No se pudo cargar el perfil' : displayName}
            </div>
            {profileFetchError ? (
              <div style={{ fontSize: 11, color: C.warn, marginTop: 6, lineHeight: 1.4 }}>{profileFetchError}</div>
            ) : null}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
              {handle}
              {locationLine ? ` · ${locationLine}` : ''}
              {profile?.public_show_phone && profile?.phone_verified_at && String(profile?.phone || '').trim()
                ? ` · Tel. ${String(profile.phone).trim()}`
                : ''}
            </div>
            {profile?.bio ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 8, lineHeight: 1.45 }}>{profile.bio}</div>
            ) : null}
            {(() => {
              const links = collectProfileSocialEntries(profile);
              if (!links.length) return null;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 8, alignItems: 'center' }}>
                  {links.map(([label, url]) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 10, color: C.gold, textDecoration: 'underline' }}>
                      {label}
                    </a>
                  ))}
                </div>
              );
            })()}
            <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.36)', lineHeight: 1.45 }}>
              Teléfono y enlaces que ves aquí respetan tu visibilidad en{' '}
              <button
                type="button"
                onClick={() => onNavigate?.('ajustes', { initialSub: 'privacidad' })}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: C.gold,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                }}>
                Ajustes → Privacidad
              </button>
              {' '}
              (y web/redes en Datos de la cuenta).
            </div>
            <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
              {isDreamvinylVerifiedPartner({
                isVerified: profile?.is_verified,
                isStore: profile?.is_store,
                partnerRoleKeys: profile?.partner_active_roles,
              }) ? (
                <PartnerVerifiedBadge size={18} />
              ) : null}
              <Badge type="gold">
                {profileLevelLabel(level)} · Nv.{level}
              </Badge>
            </div>
            <CosmeticBadgesRow badges={cosmeticBadgesEquipped} style={{ marginTop: 6 }} />
          </div>
          <div
            style={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0 10px',
              minWidth: PERFIL_HEADER_AVATAR_PX,
              alignSelf: 'stretch',
            }}>
            <SurcosRating value={groovesSurcosVisuales} readOnly sizePx={PERFIL_HEADER_AVATAR_PX} showLabel={false} />
            <div
              style={{
                marginTop: 8,
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: C.gold,
                lineHeight: 1.3,
                maxWidth: 130,
                fontFamily: 'Georgia, serif',
              }}>
              {profileLoading ? '…' : groovesLevelDream.label}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!user) {
                onNavigate('marketplace');
                return;
              }
              onNavigate('ajustes', { initialSub: 'cuenta' });
            }}
            style={{
              background: 'transparent',
              border: '0.5px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.7)',
              padding: '7px 14px',
              borderRadius: 10,
              fontSize: 11,
              cursor: 'pointer',
              flexShrink: 0,
            }}>
            Editar
          </button>
        </div>

        <DCard style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.gold }}>Mis Grooves</div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10, alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>Saldo actual</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>{profileLoading ? '…' : groovesTotal}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>Nivel (actividad)</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: C.gold }}>{profileLoading ? '…' : groovesLevelDream.label}</div>
            </div>
          </div>
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '0 0 10px', lineHeight: 1.45 }}>
            El nivel y los retos avanzan con Grooves ganados en la plataforma. Las compras suman al saldo para canjes, no al nivel.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.75)' }}>Progreso de nivel</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: C.gold }}>
              {gProg.isMax ? `${groovesEarned}` : `${groovesEarned} / ${gProg.nextLevelAt}`}
            </span>
          </div>
          <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
            <div style={{ height: '100%', width: `${gProg.isMax ? 100 : gProg.pct}%`, background: C.gold, borderRadius: 3, transition: 'width .3s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 10, color: C.gold }}>
              {profileLevelLabel(level)}
              {gProg.isMax ? ' — máximo nivel de esta escala' : ''}
            </span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
              {gProg.isMax ? 'Sigue sumando grooves' : `${Math.max(0, gProg.nextLevelAt - groovesEarned)} para el siguiente tramo`}
            </span>
          </div>
          {groovesPremiados.length ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Insignias de ranking</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {groovesPremiados.map((b, i) => (
                  <span
                    key={`${b.period_key}-${b.badge_slug}-${i}`}
                    title={b.period_key ? String(b.period_key) : ''}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 600,
                      border: '0.5px solid rgba(200,168,75,0.35)',
                      color: C.gold,
                      background: 'rgba(200,168,75,0.1)',
                    }}>
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Retos por nivel</div>
            {challengesLoading ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Cargando retos…</div>
            ) : !challengesByLevel.length ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Próximamente verás aquí los retos de tu nivel.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {challengesByLevel.map(([lv, rows]) => {
                  const open = expandedChallengeLevels.has(lv);
                  const completedCount = rows.filter(c => challengeCompleteIds.has(c.id)).length;
                  const isCurrentLevel = lv === level;
                  return (
                    <div
                      key={lv}
                      style={{
                        borderRadius: 10,
                        border: `0.5px solid ${isCurrentLevel ? 'rgba(200,168,75,0.35)' : 'rgba(255,255,255,0.1)'}`,
                        background: 'rgba(255,255,255,0.03)',
                        overflow: 'hidden',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleChallengeLevel(lv)}
                        aria-expanded={open}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 12px',
                          border: 'none',
                          background: open ? 'rgba(200,168,75,0.08)' : 'transparent',
                          color: '#fff',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'inherit',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            flexShrink: 0,
                          }}
                          aria-hidden
                        >
                          <Icon name="arrow_r" size={14} color={C.gold} />
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: C.gold }}>
                            Nivel {lv} · {profileLevelLabel(lv)}
                          </span>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>
                            {rows.length} {rows.length === 1 ? 'reto' : 'retos'}
                            {completedCount > 0 ? ` · ${completedCount} completado${completedCount === 1 ? '' : 's'}` : ''}
                          </span>
                        </span>
                        {isCurrentLevel ? (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: C.gold,
                              padding: '2px 6px',
                              borderRadius: 4,
                              border: '0.5px solid rgba(200,168,75,0.35)',
                              flexShrink: 0,
                            }}>
                            Tu nivel
                          </span>
                        ) : null}
                      </button>
                      {open ? (
                        <div style={{ padding: '0 10px 10px' }}>
                          {rows.map(c => {
                    const unlocked = challengeUnlockIds.has(c.id);
                    const lockedByLevel = lv > level;
                    const completed = challengeCompleteIds.has(c.id);
                    const inProgress = unlocked && !completed && !lockedByLevel;
                    return (
                      <div
                        key={c.id}
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          padding: '8px 10px',
                          marginBottom: 6,
                          borderRadius: 8,
                          background: completed
                            ? 'rgba(29,158,117,0.08)'
                            : inProgress
                              ? 'rgba(200,168,75,0.06)'
                              : 'rgba(255,255,255,0.04)',
                          border: `0.5px solid ${
                            completed
                              ? 'rgba(29,158,117,0.25)'
                              : inProgress
                                ? 'rgba(200,168,75,0.25)'
                                : 'rgba(255,255,255,0.08)'
                          }`,
                          opacity: lockedByLevel ? 0.55 : 1,
                        }}
                      >
                        {completed ? (
                          <div
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              background: 'rgba(29,158,117,0.2)',
                              border: '0.5px solid rgba(29,158,117,0.45)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              marginTop: 1,
                            }}
                            title="Completado"
                            aria-hidden
                          >
                            <Icon name="check" size={13} color={C.green} />
                          </div>
                        ) : null}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{c.title}</div>
                          {c.description ? (
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2, lineHeight: 1.4 }}>{c.description}</div>
                          ) : null}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 10, color: C.gold, fontWeight: 600 }}>+{c.grooves_reward} G</div>
                          <div style={{ fontSize: 9, color: completed ? C.green : 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                            {lockedByLevel
                              ? `Nv.${lv}`
                              : completed
                                ? 'Completado'
                                : inProgress
                                  ? 'En progreso'
                                  : unlocked
                                    ? 'Disponible'
                                    : 'Al subir de nivel'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {user?.id && profile?.phone_verified_at ? (
            <GroovesCanjeShop
              userId={user.id}
              profile={profile}
              phoneVerified={!!profile?.phone_verified_at}
              groovesBalance={groovesTotal}
              onBalanceChange={() => refetchProfile()}
              onStatsPurchased={() => {
                refetchProfile();
                fetchProfileCosmeticBadges(user.id).then(r => setCosmeticBadgesEquipped(r.equipped ?? []));
              }}
              onCosmeticChange={() =>
                fetchProfileCosmeticBadges(user.id).then(r => setCosmeticBadgesEquipped(r.equipped ?? []))
              }
            />
          ) : null}
          {user && profile?.phone_verified_at ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Comprar Grooves</div>
              {!paymentsEnabled ? (
                <div
                  style={{
                    fontSize: 11,
                    color: 'rgba(200,168,75,0.9)',
                    lineHeight: 1.5,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: 'rgba(200,168,75,0.08)',
                    border: '0.5px solid rgba(200,168,75,0.28)',
                  }}
                >
                  La compra de paquetes Grooves con tarjeta estará disponible en la siguiente fase del lanzamiento.
                  Mientras tanto puedes ganar Grooves con la actividad en la app (escaneos, retos, etc.).
                </div>
              ) : null}
              {paymentsEnabled ? (
              <>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.45, marginBottom: 10 }}>
                Elige un paquete. El pago se completa en Stripe; los Grooves se acreditan al confirmar el cobro.
              </div>
              {grooveShopErr ? (
                <div
                  style={{
                    fontSize: 11,
                    color: C.danger,
                    marginBottom: 8,
                    lineHeight: 1.4,
                    background: 'rgba(226,75,74,0.08)',
                    padding: '8px 10px',
                    borderRadius: 8,
                  }}>
                  {grooveShopErr}
                </div>
              ) : null}
              {grooveShopMsg ? (
                <div style={{ fontSize: 11, color: 'rgba(200,168,75,0.95)', marginBottom: 8, lineHeight: 1.4 }}>{grooveShopMsg}</div>
              ) : null}
              {groovePacksLoading ? (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Cargando paquetes…</div>
              ) : groovePacks.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groovePacks.map(p => {
                    const priceLabel = groovesPackDisplayPrice(p) || '—';
                    const busy = groovePackBusy === p.code;
                    return (
                      <div
                        key={p.code}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 12px',
                          borderRadius: 10,
                          background: 'rgba(255,255,255,0.04)',
                          border: '0.5px solid rgba(200,168,75,0.2)',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', lineHeight: 1.35 }}>
                            {groovesPackDisplayLabel(p)}
                          </div>
                          <div style={{ fontSize: 11, color: C.gold, marginTop: 4, fontWeight: 600 }}>{priceLabel}</div>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleBuyGroovesPack(p.code)}
                          style={{
                            flexShrink: 0,
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: `0.5px solid ${C.gold}`,
                            background: busy ? 'rgba(200,168,75,0.08)' : 'rgba(200,168,75,0.18)',
                            color: C.gold,
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: busy ? 'wait' : 'pointer',
                            opacity: busy ? 0.7 : 1,
                          }}
                        >
                          {busy ? '…' : 'Comprar'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
                  No hay paquetes activos con precio Stripe configurado.
                </div>
              )}
              </>
              ) : null}
            </div>
          ) : null}
          {!profile?.phone_verified_at ? (
            <div
              style={{
                padding: 12,
                borderRadius: 10,
                background: 'rgba(239,159,39,0.08)',
                border: '0.5px solid rgba(239,159,39,0.25)',
                fontSize: 12,
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.45,
              }}>
              <div style={{ marginBottom: 10 }}>{GROOVES_PHONE_GATE_USER_MESSAGE}</div>
              <button
                type="button"
                onClick={() => onNavigate('ajustes', { initialSub: 'cuenta' })}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: `0.5px solid ${C.gold}`,
                  background: 'rgba(200,168,75,0.12)',
                  color: C.gold,
                  fontSize: 12,
                  cursor: 'pointer',
                }}>
                Ir a Ajustes
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Últimos movimientos</div>
              {groovesLedgerLoading && groovesLedgerWithBalance.length === 0 ? (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Cargando…</div>
              ) : groovesLedgerWithBalance.length === 0 ? (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Aún no hay movimientos en tu historial.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {groovesLedgerWithBalance.map(row => {
                    const d = Number(row.delta) || 0;
                    const pos = d >= 0;
                    const concept = groovesConfigDesc[row.reason] || GROOVES_LEDGER_REASON_LABELS[row.reason] || row.reason;
                    const fecha = row.created_at
                      ? new Date(row.created_at).toLocaleString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—';
                    return (
                      <div
                        key={row.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto auto',
                          gap: 8,
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: '0.5px solid rgba(255,255,255,0.06)',
                          fontSize: 11,
                        }}>
                        <span style={{ color: 'rgba(255,255,255,0.75)', minWidth: 0 }}>{fecha}</span>
                        <span style={{ color: 'rgba(255,255,255,0.55)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={concept}>
                          {concept}
                        </span>
                        <span style={{ fontWeight: 600, color: pos ? C.green : C.danger, textAlign: 'right' }}>
                          {pos ? '+' : ''}
                          {d}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.45)', textAlign: 'right' }}>{row.balanceAfter}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {groovesLedgerHasMore ? (
                <BtnGold
                  type="button"
                  disabled={groovesLedgerLoading}
                  onClick={loadMoreGroovesLedger}
                  style={{ marginTop: 12, padding: '8px 14px', fontSize: 11, borderRadius: 8, opacity: groovesLedgerLoading ? 0.6 : 1 }}>
                  {groovesLedgerLoading ? 'Cargando…' : 'Ver más'}
                </BtnGold>
              ) : null}
            </div>
          )}
        </DCard>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
          {[
            [statLabel(stats.ventas), 'Ventas'],
            [statLabel(stats.compras), 'Compras'],
            [statLabel(stats.seguidores), 'Seguidores'],
          ].map(([n, l]) => (
            <DCard key={l} style={{ padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: C.gold }}>{n}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{l}</div>
            </DCard>
          ))}
          <DCard style={{ padding: '10px 6px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <SurcosRating value={valoracionSurcos} readOnly size="sm" />
            <div style={{ fontSize: 17, fontWeight: 600, color: C.gold, lineHeight: 1 }}>
              {valoracionMedia != null ? valoracionMedia : '—'}
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>Valoración</div>
            <button
              type="button"
              onClick={() => onNavigate('valoraciones')}
              style={{
                marginTop: 4,
                background: 'none',
                border: 'none',
                color: 'rgba(200,168,75,0.65)',
                fontSize: 9,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}>
              {rating.count ? `Ver detalle (${rating.count})` : 'Sin valoraciones aún'}
            </button>
          </DCard>
        </div>

        <DCard style={{ padding: '4px 16px', marginBottom: 16 }}>
          {[
            { icon: 'bag', ic: 'gold', color: C.gold, title: 'Mis ventas', sub: 'Anuncios, subastas, pedidos', nav: 'misventas' },
            { icon: 'bag', ic: 'blue', color: '#85B7EB', title: 'Mis compras', sub: 'Pedidos y historial', nav: 'pedido' },
            { icon: 'info', ic: 'gold', color: C.gold, title: 'Mis solicitudes', sub: 'Partner, Comunidad y revisiones', nav: 'mis-solicitudes' },
            { icon: 'heart', ic: 'purple', color: C.purpleL, title: 'Mis favoritos', sub: 'Discos con ❤️ en el mercado', nav: 'marketplace', openFavorites: true },
            { icon: 'bookmark', ic: 'gold', color: C.gold, title: 'Guardados (Comunidad)', sub: 'Eventos y lugares guardados', nav: 'comunidad-guardados' },
            { icon: 'disc', ic: 'green', color: C.green, title: 'Mi colección', sub: 'Discos que tienes y no vendes', action: 'coleccion' },
            { icon: 'chat', ic: 'coral', color: '#F0997B', title: 'Mensajes', sub: 'Chats activos', nav: 'chat' },
            { icon: 'card', ic: 'gold', color: C.gold, title: 'Métodos de pago', sub: 'Tarjeta, Bizum, monedero', nav: 'ajustes' },
            { icon: 'settings', ic: 'gray', color: 'rgba(255,255,255,0.5)', title: 'Ajustes y privacidad', sub: 'Cuenta, notificaciones, legal', nav: 'ajustes' },
          ].map(item => (
            <SettingsRow
              key={item.title}
              icon={item.icon}
              iconBg={`rgba(${item.ic === 'gold' ? '200,168,75' : item.ic === 'blue' ? '55,138,221' : item.ic === 'purple' ? '83,74,183' : item.ic === 'green' ? '29,158,117' : item.ic === 'coral' ? '216,90,48' : '255,255,255'},0.15)`}
              iconColor={item.color}
              title={item.title}
              sub={item.sub}
              right={<span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 16 }}>›</span>}
              onClick={() => {
                if (item.action === 'coleccion') {
                  setTab(1);
                  return;
                }
                goNav(item);
              }}
            />
          ))}
          <SettingsRow
            icon="logout"
            iconBg="rgba(226,75,74,0.1)"
            iconColor={C.danger}
            title="Cerrar sesión"
            right={null}
            onClick={async () => {
              const { error } = await signOutApp();
              if (error) console.warn('Cerrar sesión:', error.message);
              onBack();
            }}
          />
        </DCard>

        <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.1)', marginBottom: 14 }}>
          {tabs.map((t, i) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(i)}
              style={{
                padding: '10px 0',
                fontSize: 11,
                color: tab === i ? C.gold : 'rgba(255,255,255,0.45)',
                cursor: 'pointer',
                marginRight: 16,
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${tab === i ? C.gold : 'transparent'}`,
                whiteSpace: 'nowrap',
              }}>
              {t}
            </button>
          ))}
        </div>

        {tab === 0 && listingsLoading && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '12px 0' }}>Cargando anuncios…</div>
        )}
        {tab === 0 && !listingsLoading && itemsByTab[0].length === 0 && (
          <DCard style={{ padding: 16, marginBottom: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
              No tienes discos en venta ahora mismo. Publica uno desde el mercado con <strong style={{ color: C.gold }}>+ VENDER</strong>.
            </p>
          </DCard>
        )}
        {tab === 0 &&
          itemsByTab[0].map((it, i) => (
            <DCard key={it.id || i} style={{ marginBottom: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 10, padding: '10px 12px' }}>
              <div style={{ width: 48, height: 48, borderRadius: 8, background: '#2d1b69', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {it.coverUrl ? (
                  <img src={it.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Icon name="disc" size={16} color="rgba(255,255,255,0.3)" />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>{it.title}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                  {it.artist} · {it.cond}
                </div>
                <div style={{ fontSize: 12, color: C.gold, marginTop: 3 }}>{it.price}</div>
              </div>
              </div>
              {it.listing && canSellerEditListing(it.listing) ? (
                <div style={{ display: 'flex', borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                  <button
                    type="button"
                    onClick={() => onNavigate('publicar', { editListing: it.listing, returnTo: 'perfil' })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: 11,
                      cursor: 'pointer',
                      color: C.gold,
                      borderRight: `0.5px solid rgba(255,255,255,0.05)`,
                    }}>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigate('ficha', it.listing)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: 11,
                      cursor: 'pointer',
                      color: 'rgba(255,255,255,0.65)',
                    }}>
                    Ver anuncio
                  </button>
                </div>
              ) : null}
            </DCard>
          ))}

        {tab === 1 && !user && (
          <DCard style={{ padding: 16, marginBottom: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>Inicia sesión para ver y gestionar tu colección.</p>
          </DCard>
        )}

        {tab === 1 && user && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, maxWidth: 420 }}>
                Discos que tienes en casa y no están en venta.                 Marca <strong style={{ color: C.gold }}>Joya</strong> o <strong style={{ color: C.gold }}>Abierto a ofertas</strong> en cada ficha.
                Pulsa <strong style={{ color: C.gold }}>+ Añadir disco</strong> para abrir el mismo flujo que al publicar (manual o IA, fotos y ficha completa), adaptado a colección.
              </p>
              <BtnGold
                onClick={() => {
                  setColeccionErr('');
                  setAddColeccionOpen(true);
                }}
                style={{ padding: '8px 14px', fontSize: 11, borderRadius: 8, whiteSpace: 'nowrap' }}>
                + Añadir disco
              </BtnGold>
            </div>
            {coleccionErr ? (
              <DCard style={{ padding: 14, marginBottom: 10, borderColor: 'rgba(226,75,74,0.35)', background: 'rgba(226,75,74,0.06)' }}>
                <p style={{ margin: 0, fontSize: 12, color: C.danger, lineHeight: 1.5 }}>{coleccionErr}</p>
              </DCard>
            ) : null}
            {coleccionLoading && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '12px 0' }}>Cargando colección…</div>
            )}
            {!coleccionLoading && !coleccionErr && coleccion.length === 0 && (
              <DCard style={{ padding: 16, marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                  Aún no has añadido ningún disco. Pulsa <strong style={{ color: C.gold }}>+ Añadir disco</strong> para crear tu primera ficha de colección.
                </p>
              </DCard>
            )}
            {!coleccionLoading &&
              coleccion.map(row => (
                <DCard
                  key={row.id}
                  style={{ display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 8, alignItems: 'flex-start' }}>
                  <ColeccionItemThumb row={row} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{row.title}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', marginTop: 3 }}>{row.artist}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', marginTop: 4 }}>
                      {coleccionMetaLine(row) ||
                        [row.release_year ? `Año ${row.release_year}` : null, row.format || null, row.condition_text || null]
                          .filter(Boolean)
                          .join(' · ') ||
                        'Sin detalles extra'}
                    </div>
                    {row.description || row.notes ? (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', marginTop: 6, lineHeight: 1.4 }}>
                        {row.description || row.notes}
                      </div>
                    ) : null}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px 18px',
                        marginTop: 10,
                        alignItems: 'center',
                        borderTop: '0.5px solid rgba(255,255,255,0.06)',
                        paddingTop: 10,
                      }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                        <Toggle
                          checked={!!row.is_joya}
                          onChange={e => handleColeccionFlag(row.id, { is_joya: e.target.checked })}
                        />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.48)' }}>Joya</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                        <Toggle
                          checked={!!row.open_to_offers}
                          onChange={e => handleColeccionFlag(row.id, { open_to_offers: e.target.checked })}
                        />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.48)' }}>Abierto a ofertas</span>
                      </label>
                      <BtnGold
                        type="button"
                        onClick={() => onNavigate('publicar', { fromColeccionItem: row })}
                        style={{ padding: '7px 12px', fontSize: 10, borderRadius: 8, marginLeft: 'auto' }}>
                        Publicar en ventas
                      </BtnGold>
                    </div>
                  </div>
                  <button
                    type="button"
                    title="Quitar de la colección"
                    onClick={async () => {
                      if (!user?.id) return;
                      try {
                        await deleteProfileColeccionItem(user.id, row.id);
                        setColeccion(prev => prev.filter(x => x.id !== row.id));
                      } catch (e) {
                        setColeccionErr(e?.message || 'No se pudo eliminar.');
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 6,
                      color: 'rgba(255,255,255,0.35)',
                      flexShrink: 0,
                    }}>
                    <Icon name="x" size={16} color="rgba(226,75,74,0.75)" />
                  </button>
                </DCard>
              ))}
          </>
        )}

        {tab === 2 && !user && (
          <DCard style={{ padding: 16, marginBottom: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>Inicia sesión para ver tus joyas.</p>
          </DCard>
        )}

        {tab === 3 && !user && (
          <DCard style={{ padding: 16, marginBottom: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>Inicia sesión para ver tus discos abiertos a ofertas.</p>
          </DCard>
        )}

        {tab === 2 && user && (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 11, color: 'rgba(255,255,255,0.42)', lineHeight: 1.5 }}>
              Piezas de tu colección que marcaste como <strong style={{ color: C.gold }}>Joya</strong>.
            </p>
            {coleccionLoading && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '12px 0' }}>Cargando…</div>
            )}
            {!coleccionLoading && joyasItems.length === 0 && (
              <DCard style={{ padding: 16, marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                  Aún no tienes joyas. En <strong style={{ color: C.gold }}>Colección</strong>, activa «Joya» en los discos que quieras destacar.
                </p>
              </DCard>
            )}
            {!coleccionLoading &&
              joyasItems.map(row => (
                <DCard
                  key={row.id}
                  style={{ display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 8, alignItems: 'flex-start' }}>
                  <ColeccionItemThumb row={row} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{row.title}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', marginTop: 3 }}>{row.artist}</div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px 18px',
                        marginTop: 10,
                        alignItems: 'center',
                      }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                        <Toggle
                          checked={!!row.is_joya}
                          onChange={e => handleColeccionFlag(row.id, { is_joya: e.target.checked })}
                        />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.48)' }}>Joya</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                        <Toggle
                          checked={!!row.open_to_offers}
                          onChange={e => handleColeccionFlag(row.id, { open_to_offers: e.target.checked })}
                        />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.48)' }}>Abierto a ofertas</span>
                      </label>
                    </div>
                  </div>
                </DCard>
              ))}
          </>
        )}

        {tab === 3 && user && (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 11, color: 'rgba(255,255,255,0.42)', lineHeight: 1.5 }}>
              Discos de tu colección con los que estás <strong style={{ color: C.gold }}>dispuesto a escuchar ofertas</strong>.
            </p>
            {coleccionLoading && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '12px 0' }}>Cargando…</div>
            )}
            {!coleccionLoading && ofertasItems.length === 0 && (
              <DCard style={{ padding: 16, marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                  Aún no marcaste ningún disco como abierto a ofertas. Actívalo en <strong style={{ color: C.gold }}>Colección</strong>.
                </p>
              </DCard>
            )}
            {!coleccionLoading &&
              ofertasItems.map(row => (
                <DCard
                  key={row.id}
                  style={{ display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 8, alignItems: 'flex-start' }}>
                  <ColeccionItemThumb row={row} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{row.title}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', marginTop: 3 }}>{row.artist}</div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px 18px',
                        marginTop: 10,
                        alignItems: 'center',
                      }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                        <Toggle
                          checked={!!row.is_joya}
                          onChange={e => handleColeccionFlag(row.id, { is_joya: e.target.checked })}
                        />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.48)' }}>Joya</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                        <Toggle
                          checked={!!row.open_to_offers}
                          onChange={e => handleColeccionFlag(row.id, { open_to_offers: e.target.checked })}
                        />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.48)' }}>Abierto a ofertas</span>
                      </label>
                    </div>
                  </div>
                </DCard>
              ))}
          </>
        )}

        <ColeccionItemFormModal
          open={addColeccionOpen && !!user}
          onClose={() => setAddColeccionOpen(false)}
          user={user}
          onNavigate={onNavigate}
          onSaved={row => {
            setColeccion(prev => [row, ...prev]);
            setColeccionErr('');
          }}
        />
      </div>
    </div>
  );
}
